import { PenIcon, PlusIcon, TrashIcon } from "@xola/icons";
import { Label, numberFormat, Table, Tooltip } from "@xola/ui-kit";
import { isPackage, SecondaryButton } from "common";
import { omit, uniqueId } from "lodash";
import React, { useEffect, useState } from "react";
import {
    Affiliate,
    CouponAlgorithm,
    CouponScope,
    ExperienceStatus,
    Product,
    ProductTermException,
    TermsTemplate,
    TermsTemplateFee,
} from "types";
import { useProduct } from "../../../hooks/resources/useProduct";
import { useSeller } from "../../../hooks/useAccount";
import { useModal } from "../../../hooks/useVisibility";
import { defaultProductTermException } from "../../../models/ProductTermException";
import { InlineConfirmDeletePopover } from "../../Common/InlineConfirmDeletePopover";
import { PromptModal } from "../../Common/PromptModal";
import { EditProductTermExceptionModal } from "./EditProductTermExceptionModal";

interface ProductTermExceptionFormTableProps {
    values: TermsTemplate;
    experiences: Product[];
    packages: Product[];
    linkedAgents?: Array<Affiliate | undefined>;
    setFieldValue: (key: string, value: ProductTermException[]) => void;
}

export const ProductTermExceptionFormTable = ({
    values,
    experiences,
    packages,
    linkedAgents,
    setFieldValue,
}: ProductTermExceptionFormTableProps) => {
    const [isSaveAsNew, setIsSaveAsNew] = useState(false);
    const [productTermExceptions, setProductTermExceptions] = useState<ProductTermException[]>([]);
    const selectedProductIds = getProductIdsFromTermExceptions(productTermExceptions);
    const [selectedProductTermException, setSelectedProductTermException] = useState<ProductTermException | null>(null);
    const availableExperiences = getAvailableProductForException(
        isSaveAsNew,
        selectedProductTermException,
        experiences,
        selectedProductIds,
    );
    const availablePackages = getAvailableProductForException(
        isSaveAsNew,
        selectedProductTermException,
        packages,
        selectedProductIds,
    );

    // we could get empty array for productTermExceptions on first load, using useEffect to set product term exceptions if values change.
    useEffect(() => {
        if (values.productTermExceptions.length > 0) {
            setProductTermExceptions(values.productTermExceptions);
        }
    }, [values]);

    const initializeNewProductTermException = () => {
        setIsSaveAsNew(true);
        setSelectedProductTermException(defaultProductTermException);
    };

    const handleFormSubmit = (value: ProductTermException) => {
        // to ensure formik recognizes the changes done to values, cloning the product term exceptions.
        let expections = [...productTermExceptions];
        if (value.id || value.exceptionIndex !== undefined || !isSaveAsNew) {
            // loop through all the exceptions to find the one being edited and switch it out for the edited values.
            expections = [
                ...expections.map((exception, index) => {
                    if ((exception.id && exception.id === value.id) || value.exceptionIndex === index) {
                        return omit(value, "exceptionIndex");
                    }

                    return exception;
                }),
            ];
        } else {
            expections.push(value);
        }

        // set the form field and update the state variable to refresh the product term exception table
        setFieldValue("productTermExceptions", [...expections]);
        setProductTermExceptions(expections);
        setIsSaveAsNew(false);
        setSelectedProductTermException(null);
    };

    const handleEdit = (index: number, productTermExceptions: ProductTermException) => {
        setSelectedProductTermException({
            ...productTermExceptions,
            exceptionIndex: index,
        });
    };

    const handleDelete = (index: number) => {
        // set the form field and update the state variable to refresh the product term exception table
        setFieldValue("productTermExceptions", [
            ...productTermExceptions.filter((exception, index_) => index !== index_),
        ]);
        setProductTermExceptions([...productTermExceptions.filter((exception, index_) => index !== index_)]);
    };

    const handleClose = () => {
        setIsSaveAsNew(false);
        setSelectedProductTermException(null);
    };

    return (
        <div className="mb-8">
            <Label>Commercial Terms Exceptions</Label>
            <Table className="mt-2">
                <Table.Head>
                    <Table.Row>
                        <Table.Header>Products</Table.Header>
                        <Table.Header>Discount</Table.Header>
                        <Table.Header>Commission</Table.Header>
                        <Table.Header>Action</Table.Header>
                    </Table.Row>
                </Table.Head>
                <Table.Body>
                    {productTermExceptions.length > 0 ? (
                        productTermExceptions.map((productTermException: ProductTermException, index: number) => {
                            return (
                                <Table.Row
                                    key={productTermException?.id ?? uniqueId(String(index))}
                                    className="justify-between border-b border-gray-lighter"
                                >
                                    <TermException
                                        productTermException={productTermException}
                                        linkedAgents={linkedAgents}
                                        onEdit={() => handleEdit(index, productTermException)}
                                        onDelete={() => handleDelete(index)}
                                    />
                                </Table.Row>
                            );
                        })
                    ) : (
                        <Table.Row>
                            <Table.Cell colSpan={4}>
                                <div className="flex flex-col items-center justify-self-center py-6">
                                    <span className="mb-4 text-sm">No exceptions configured</span>
                                    <SecondaryButton
                                        icon={<PlusIcon size="small" />}
                                        size="small"
                                        onClick={() => initializeNewProductTermException()}
                                    >
                                        Add Exception
                                    </SecondaryButton>
                                </div>
                            </Table.Cell>
                        </Table.Row>
                    )}
                </Table.Body>
            </Table>

            {productTermExceptions.length > 0 ? (
                <div className="mt-4 flex justify-end">
                    <SecondaryButton
                        icon={<PlusIcon size="small" />}
                        size="small"
                        onClick={() => initializeNewProductTermException()}
                    >
                        Add Exception
                    </SecondaryButton>
                </div>
            ) : null}

            {selectedProductTermException && (
                <EditProductTermExceptionModal
                    title={isSaveAsNew ? "Add Exception" : "Edit Exception"}
                    isOpen={!!selectedProductTermException}
                    experiences={availableExperiences}
                    packages={availablePackages}
                    productTermException={selectedProductTermException}
                    onClose={handleClose}
                    onSubmit={handleFormSubmit}
                />
            )}
        </div>
    );
};

interface TermExceptionProps {
    productTermException: ProductTermException;
    linkedAgents?: Array<Affiliate | undefined>;
    onEdit: () => void;
    onDelete: () => void;
}

const TermException = ({ productTermException, linkedAgents, onEdit, onDelete }: TermExceptionProps) => {
    const seller = useSeller();
    const confirmDeleteModal = useModal();
    const productCount = productTermException.products.length;

    const { data: product, isLoading: isLoadingProduct } = useProduct(productTermException.products[0]);
    if (isLoadingProduct) {
        return null;
    }

    const productLabel = generateProductLabel(productCount, product!);
    const discountText = getTermExceptionAmountText(productTermException.discount, seller.currency);
    const commissionText = getTermExceptionAmountText(productTermException.commission, seller.currency);
    const linkedAgentNames = linkedAgents
        ?.map((agent) => agent?.name)
        .filter(Boolean)
        .join(", ");
    return (
        <>
            <Table.Cell className="!whitespace-normal" data-testid="product-term-exception-row-products">
                <div className="w-30 break-all">
                    {productCount > 1 ? (
                        <Tooltip
                            placement="bottom"
                            content={<ProductList productIds={productTermException.products} />}
                        >
                            <span className="underline">{productLabel}</span>
                        </Tooltip>
                    ) : (
                        productLabel
                    )}
                </div>
            </Table.Cell>

            <Table.Cell className="!whitespace-normal" data-testid="product-term-exception-row-discount">
                <div className="w-30">{discountText}</div>
            </Table.Cell>

            <Table.Cell className="!whitespace-normal" data-testid="product-term-exception-row-commission">
                <div className="w-30">{commissionText}</div>
            </Table.Cell>

            <Table.Cell>
                <div className="flex items-center justify-start space-x-2">
                    <SecondaryButton size="tiny" onClick={onEdit}>
                        <PenIcon />
                    </SecondaryButton>
                    {linkedAgents && linkedAgents.length > 0 ? (
                        <SecondaryButton size="tiny" onClick={confirmDeleteModal.toggle}>
                            <TrashIcon />
                        </SecondaryButton>
                    ) : (
                        <InlineConfirmDeletePopover confirmButtonText="Confirm" onConfirm={() => onDelete()}>
                            <SecondaryButton size="tiny">
                                <TrashIcon />
                            </SecondaryButton>
                        </InlineConfirmDeletePopover>
                    )}

                    <PromptModal
                        title="Delete term exception?"
                        isOpen={confirmDeleteModal.isVisible}
                        onConfirm={() => onDelete()}
                        onClose={confirmDeleteModal.hide}
                    >
                        Are you sure you want to delete this commercial term exception?{" "}
                        {linkedAgents?.length
                            ? `All new purchases using this agent code will follow the discount and commission structure for ${linkedAgentNames}.`
                            : ""}
                    </PromptModal>
                </div>
            </Table.Cell>
        </>
    );
};

const ProductList = ({ productIds }: { productIds: ID[] }) => {
    return (
        <ul className="no-translate list-disc pl-5">
            {productIds.map((productId) => (
                <ProductItem key={productId} productId={productId} />
            ))}
        </ul>
    );
};

const ProductItem = ({ productId }: { productId: ID }) => {
    const { data: product } = useProduct(productId);
    return <li>{product?.name}</li>;
};

const getTermExceptionAmountText = (templateFee: TermsTemplateFee, currency: string) => {
    const summary = [
        templateFee.scope === CouponScope.PER_BOOKING ? "per reservation" : "per person",
        templateFee.includeAddOns ? "with add-ons" : "",
    ].join(" ");

    return (
        <span>
            <var data-var="exceptionAmount">
                {templateFee.algorithm === CouponAlgorithm.ABSOLUTE
                    ? numberFormat(templateFee.amount, currency)
                    : `${templateFee.amount}%`}
            </var>{" "}
            {summary}
        </span>
    );
};

const getProductIdsFromTermExceptions = (productTermExceptions: ProductTermException[]) => {
    let productIds: string[] = [];
    for (const exception of productTermExceptions) {
        productIds = [...productIds, ...exception.products];
    }

    return productIds;
};

const getAvailableProductForException = (
    isSaveAsNew: boolean,
    productTermException: ProductTermException | null,
    products: Product[],
    selectedProductIds: ID[],
) => {
    // filter out products that are not published or are already associated with a exception
    const availableProducts = products
        .filter(
            (product) =>
                (product.status === ExperienceStatus.PUBLISHED || isPackage(product)) &&
                !selectedProductIds.includes(product.id),
        )
        .map((product) => {
            return {
                id: product.id,
                name: product.name,
            };
        });

    if (isSaveAsNew || !productTermException) {
        return availableProducts;
    }

    const selectedProducts = products
        .filter((product) => productTermException.products.includes(product.id))
        .map((product) => {
            return {
                id: product.id,
                name: product.name,
            };
        });

    return [...selectedProducts, ...availableProducts];
};

// If there is more than 1 product, we return the count else return the product name.
const generateProductLabel = (count: number, product: Product) => {
    if (count > 1) {
        return (
            <span>
                <var data-var="productCount">{count}</var> Products
            </span>
        );
    }

    return product.name.trim();
};
